import { createSelector } from "reselect";

import { Role } from "@mapmycustomers/shared";
import ResourcesVisibility from "@mapmycustomers/shared/enum/ResourcesVisibility";

import OrganizationSetting from "@app/enum/OrganizationSetting";
import { getCurrentUser, getCurrentUserId, getOrganizationSettingValue } from "@app/store/iam";
import { isOwner } from "@app/store/iam/util";
import {
  getTeams,
  getTeamsOfEntireOrganization,
  getUsers,
  getUsersOfEntireOrganization,
} from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

const resourcesState = (state: RootState) => state.scene.resources;

export const isInitializing = createSelector(resourcesState, ({ initializing }) => initializing);
export const getResourcesLoading = createSelector(
  resourcesState,
  ({ resourcesLoading }) => resourcesLoading
);
export const getFolders = createSelector(resourcesState, ({ folders }) => folders);
export const getUserFolders = createSelector(
  resourcesState,
  getCurrentUserId,
  ({ folders }, currentUserId) =>
    folders.filter(({ isTrash, userId }) => userId === currentUserId && !isTrash)
);

export const getSharedFolders = createSelector(
  resourcesState,
  getCurrentUserId,
  ({ folders }, currentUserId) =>
    folders.filter(
      ({ isTrash, userId, userIds }) =>
        currentUserId && userId !== currentUserId && userIds.includes(currentUserId) && !isTrash
    )
);

export const getResources = createSelector(resourcesState, ({ resources }) => resources);

export const getResourcesViewState = createSelector(resourcesState, ({ viewState }) => viewState);

export const getTotalResources = createSelector(resourcesState, ({ total }) => total);

export const getStorageUsed = createSelector(resourcesState, ({ storageUsed }) => storageUsed);

export const areFilesUploading = createSelector(
  resourcesState,
  ({ filesUploading }) => filesUploading
);

export const getPinnedResources = createSelector(
  resourcesState,
  ({ pinnedResources }) => pinnedResources
);

export const arePinnedResourcesUpdating = createSelector(
  resourcesState,
  ({ pinnedUpdating }) => pinnedUpdating
);

export const getUsersToShareWith = createSelector(
  getCurrentUser,
  getOrganizationSettingValue,
  getUsersOfEntireOrganization,
  getUsers,
  (currentUser, orgSettingValue, usersOfEntireOrg, users) => {
    const sharingSetting = orgSettingValue(OrganizationSetting.RESOURCE_AND_FOLDER_SHARING);
    const currentUserTeamIds = currentUser?.teams.map(({ id }) => id) ?? [];

    switch (currentUser?.role.key) {
      case Role.OWNER:
        return usersOfEntireOrg.filter(({ id }) => id !== currentUser.id);
      case Role.MANAGER:
        return sharingSetting.manager === ResourcesVisibility.NONE
          ? []
          : sharingSetting.manager === ResourcesVisibility.ALL
          ? usersOfEntireOrg.filter(({ id }) => id !== currentUser.id)
          : users.filter(
              (user) =>
                user.id !== currentUser.id &&
                (!isOwner(user) || user.teams.some(({ id }) => currentUserTeamIds.includes(id)))
            );
      case Role.MEMBER:
        return sharingSetting.member === ResourcesVisibility.NONE
          ? []
          : users.filter(
              (user) =>
                user.id !== currentUser.id &&
                (!isOwner(user) || user.teams.some(({ id }) => currentUserTeamIds.includes(id)))
            );
      default:
        return [];
    }
  }
);

export const getTeamsToShareWith = createSelector(
  getCurrentUser,
  getOrganizationSettingValue,
  getTeamsOfEntireOrganization,
  getTeams,
  (currentUser, orgSettingValue, teamsOfEntireOrg, teams) => {
    const sharingSetting = orgSettingValue(OrganizationSetting.RESOURCE_AND_FOLDER_SHARING);

    switch (currentUser?.role.key) {
      case Role.OWNER:
        return teamsOfEntireOrg;
      case Role.MANAGER:
        return sharingSetting.manager === ResourcesVisibility.NONE
          ? []
          : sharingSetting.manager === ResourcesVisibility.ALL
          ? teamsOfEntireOrg
          : teams;
      case Role.MEMBER:
        return sharingSetting.member === ResourcesVisibility.NONE ? [] : teams;
      default:
        return [];
    }
  }
);

export const isResourceOrFolderSharingAllowed = createSelector(
  getCurrentUser,
  getOrganizationSettingValue,
  (currentUser, orgSettingValue) => {
    const role = currentUser?.role.key;
    const sharingSetting = orgSettingValue(OrganizationSetting.RESOURCE_AND_FOLDER_SHARING);

    return (
      role === Role.OWNER ||
      (role === Role.MANAGER && sharingSetting.manager !== ResourcesVisibility.NONE) ||
      (role === Role.MEMBER && sharingSetting.member !== ResourcesVisibility.NONE)
    );
  }
);
